// Synthetic chain ids (Stellar has no numeric id); must match the backend and fuul-hosted-webapp
export const STELLAR_CHAIN_IDS = {
  MAINNET: 148,
  TESTNET: 1481,
} as const;

export type StellarChainId = (typeof STELLAR_CHAIN_IDS)[keyof typeof STELLAR_CHAIN_IDS];

export interface StellarNetworkConfig {
  chainId: StellarChainId;
  networkPassphrase: string;
  rpcUrls: string[]; // Soroban RPC, multiple for fallback
  name: string;
  isTestnet: boolean;
  explorerUrl: string;
  explorerName: string;
  nativeCurrencySymbol: string;
  fuulFactoryContractAddress: string | undefined;
  fuulManagerContractAddress: string | undefined;
}

export const STELLAR_NETWORKS: Record<StellarChainId, StellarNetworkConfig> = {
  [STELLAR_CHAIN_IDS.MAINNET]: {
    chainId: STELLAR_CHAIN_IDS.MAINNET,
    networkPassphrase: 'Public Global Stellar Network ; September 2015',
    rpcUrls: [process.env.NEXT_PUBLIC_STELLAR_RPC_MAINNET, 'https://soroban-rpc.mainnet.stellar.gateway.fm'].filter(
      Boolean,
    ) as string[],
    name: 'Stellar',
    isTestnet: false,
    explorerUrl: 'https://stellar.expert/explorer/public',
    explorerName: 'StellarExpert',
    nativeCurrencySymbol: 'XLM',
    fuulFactoryContractAddress: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_148,
    fuulManagerContractAddress: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_148,
  },
  [STELLAR_CHAIN_IDS.TESTNET]: {
    chainId: STELLAR_CHAIN_IDS.TESTNET,
    networkPassphrase: 'Test SDF Network ; September 2015',
    rpcUrls: [process.env.NEXT_PUBLIC_STELLAR_RPC_TESTNET, 'https://soroban-testnet.stellar.org'].filter(
      Boolean,
    ) as string[],
    name: 'Stellar Testnet',
    isTestnet: true,
    explorerUrl: 'https://stellar.expert/explorer/testnet',
    explorerName: 'StellarExpert',
    nativeCurrencySymbol: 'XLM',
    fuulFactoryContractAddress: process.env.NEXT_PUBLIC_FUUL_FACTORY_CONTRACT_ADDRESS_1481,
    fuulManagerContractAddress: process.env.NEXT_PUBLIC_FUUL_MANAGER_ADDRESS_1481,
  },
};

const STELLAR_CHAIN_ID_SET = new Set<number>(Object.values(STELLAR_CHAIN_IDS));

export function isStellarChainId(chainId: string | number | null | undefined): chainId is StellarChainId {
  if (!chainId) return false;
  const numericId = Number(chainId);
  return STELLAR_CHAIN_ID_SET.has(numericId);
}

export function getStellarNetworkConfig(chainId: string | number): StellarNetworkConfig {
  if (!isStellarChainId(chainId)) {
    throw new Error(`Invalid Stellar chainId: ${chainId}`);
  }
  return STELLAR_NETWORKS[Number(chainId) as StellarChainId];
}

// Strkeys are case-sensitive: never lowercase them
export function getStellarExplorerTxUrl(hash: string, chainId: StellarChainId | string | number): string {
  return `${getStellarNetworkConfig(chainId).explorerUrl}/tx/${hash}`;
}

export function getStellarExplorerContractUrl(contractId: string, chainId: StellarChainId | string | number): string {
  return `${getStellarNetworkConfig(chainId).explorerUrl}/contract/${contractId}`;
}

export function getStellarExplorerAccountUrl(accountId: string, chainId: StellarChainId | string | number): string {
  return `${getStellarNetworkConfig(chainId).explorerUrl}/account/${accountId}`;
}

export function getStellarExplorerAddressUrl(address: string, chainId: StellarChainId | string | number): string {
  return address.startsWith('C')
    ? getStellarExplorerContractUrl(address, chainId)
    : getStellarExplorerAccountUrl(address, chainId);
}
